import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '../templates/AppLayout/AppLayout';
import { Header } from '../layout/Header';
import { Sidebar } from '../Sidebar';
import { ErrorBoundary, ErrorFallback } from './ErrorBoundary';

/**
 * Route-level boundary (inside the router, around <Routes>). Catches errors a page
 * throws outside AppLayout's own content boundary — e.g. in its hooks before it
 * renders the layout — and shows the error card inside the app shell so header and
 * sidebar stay usable. Resets when the path changes.
 */
export function AppRouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundary
      resetKey={pathname}
      fallback={(reset) => (
        // If the shell itself is what crashed, the outer last-resort boundary takes over.
        <ErrorBoundary fallback={() => <ErrorFallback />}>
          <AppLayout
            header={<Header />}
            sidebar={<Sidebar />}
            main={
              <ErrorFallback
                onGoHome={() => {
                  reset();
                  navigate('/dashboard');
                }}
              />
            }
          />
        </ErrorBoundary>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/** Outermost boundary: never a blank page, even if providers or the router crash. */
export function AppRootErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary fallback={() => <ErrorFallback />}>{children}</ErrorBoundary>;
}
